import React from 'react';

import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import iconRightArrow from 'stile-shared/assets/icons/icon_right_arrow.svg';
import styled from 'styled-components';

import { Link } from 'stile-shared/src/components/2024/common/Link';

import { Icon } from '../Icon';

// Styled components
const Image = styled.img.attrs(() => ({
  onError: (e: React.SyntheticEvent<HTMLImageElement>) => {
    (e.target as HTMLImageElement).src =
      'https://stileeducation.com/au/static/8e43f4232292ec71fa2fe7e771eec120/83395/in_class.webp';
  },
}))`
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  border: 1px solid var(--stile-grey-2);
  box-shadow: 0px 19px 19px 0px rgba(0, 0, 0, 0.06), 0px 5px 11px 0px rgba(0, 0, 0, 0.07);
  z-index: 20;
  object-fit: cover;
  opacity: 1;

  @media (max-width: 768px) {
    grid-column: span 8;
    border-radius: 0.92em;
    border-width: 0.92px;
    box-shadow: 0px 5px 11px 0px #00000012;
  }
`;

const Group = styled.div`
  grid-column: span 4;
  width: 100%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  gap: 3px;
  margin: 0 0 60px;

  &:hover ${Image} {
    box-shadow: 0px 5px 11px 0px rgba(0, 0, 0, 0.07), 0px 19px 19px 0px rgba(0, 0, 0, 0.06);
    border: 1px solid var(--stile-green-1);
  }

  @media (max-width: 768px) {
    grid-column: span 12;
    width: 100%;
    flex-shrink: 0;
    margin: 0 0 28px;
  }
`;

const Name = styled.h6`
  margin: 17px 0 9px 0;
`;

const ImageLink = styled(Link)`
  img {
    border-radius: 12px;
    width: 100%;
  }
  .gatsby-image-wrapper {
    div {
      max-width: none !important;
    }
    width: 100%;
  }
  border-radius: 12px;
  box-shadow: 0px 19px 19px 0px rgba(0, 0, 0, 0.06), 0px 5px 11px 0px rgba(0, 0, 0, 0.07);
`;
const Description = styled.p`
  width: 100%;
  margin: 0;
  padding: 0;
`;
const Location = styled.h6`
  color: var(--stile-green-3);
`;

const StyledLink = styled(Link)`
  padding-top: 9px;
  text-decoration: none;
  display: flex;
  gap: 5px;
  align-items: center;
  /* Grey 8 in the designs, matching the arrow icon, which is already Grey 8. */
  color: var(--stile-grey-8);

  &:hover {
    color: var(--stile-green-3);
  }
`;

// Main component
interface CaseStudyTileProp {
  imgSrc: string | IGatsbyImageData;
  title: string;
  description: string;
  school: string;
  url: string;
  slug: string;
}

const CaseStudyTile: React.FC<CaseStudyTileProp> = ({
  imgSrc,
  title,
  description,
  school,
  url,
  slug,
}) => (
  <Group id={slug}>
    <ImageLink url={url} style={{ textDecoration: 'none' }}>
      {typeof imgSrc === 'string' ? (
        <Image src={imgSrc} alt={title} />
      ) : (
        <GatsbyImage image={imgSrc as IGatsbyImageData} alt={title} />
      )}
    </ImageLink>

    <Name>{title}</Name>
    <Location className="h7">{school}</Location>
    <Description>{description}</Description>
    <StyledLink url={url} variant="unstyled">
      <h6 className="h8">Read full case study</h6>
      <Icon src={iconRightArrow} iconSize="fontSize" />
    </StyledLink>
  </Group>
);

export default CaseStudyTile;
